import type { Point2d, Scale2d } from "@logic/lib/2d"
import { isSamePoint } from "@logic/lib/2d"
import type { Panel } from "@logic/lib/design"
import { screenToStudio } from "@logic/lib/design"
import { nanoid } from "nanoid"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import { getHoveredInstallArea } from "~/containers/LegacyDesigner/Studio/Overlays/PreviewPanel"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { DEFAULT_SOLAR_MODULE_EQUIPMENT } from "~/containers/Proposal/state/equipment/defaults"
import type { PricedEquipment } from "~/graphql/client"

import {
  getMousePositionOnScreen,
  readonlyTransactors,
  type TransactionOperation,
} from "."
import { clearSelection } from "./clearSelection"
import { getPanelFootprint } from "./getPanelFootprint"
import { startPointState } from "../../inputs"
import { zoomState } from "../../view"
import { findPanelState, panelIndex, selectedModuleState } from "../panels"
import { areasOfPanelsIndexState } from "../relations"
import type {
  PanelActionTo_,
  PanelChangeTo_,
  UnknownChangeForUnknownElement,
} from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const addPanel: TransactionOperation<
  Point2d & Scale2d & { r: number; orientation?: number | null },
  UnknownChangeForUnknownElement[]
> = (transactors, at) => {
  const { get, set } = transactors
  const changes: UnknownChangeForUnknownElement[] = []

  const zoom = get(zoomState)
  const { x, y, r, w, h, orientation } = at

  const rectangle = { x, y, w, h, r }
  const rectangleScaled = screenToStudio.rectangle(rectangle, zoom)
  const id = nanoid()

  const panelFootprint = getPanelFootprint(transactors, null)
  const selectedModule = get(selectedModuleState)

  const panel: Panel = {
    ...rectangleScaled,
    hOriginal: panelFootprint.h,
    wOriginal: panelFootprint.w,
    xSnap: null,
    ySnap: null,
    isActive: true,
    orientation: orientation ?? 0,
    module: selectedModule.visionId,
    moduleOsId: selectedModule.id,
  }
  set(findPanelState(id), panel)
  set(panelIndex, (s) => [...s, id])
  const action: PanelActionTo_[`CREATE`] = {
    type: `CREATE`,
    family: `PANEL`,
    id,
  }
  changes.push(
    generateElementChange(transactors, {
      action,
    }) as PanelChangeTo_[`CREATE`],
  )

  const startPoint = get(startPointState)
  const mouse = getMousePositionOnScreen(readonlyTransactors(get))
  const isDragging = startPoint && !isSamePoint(startPoint, mouse)

  if (!isDragging) {
    const hoveredInstallArea = getHoveredInstallArea(readonlyTransactors(get))
    const areaId = hoveredInstallArea?.id
    if (areaId) {
      set(areasOfPanelsIndexState, (current) => current.set(areaId, id))
      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `ATTACH`,
            family: `AREA_PANEL`,
            id: areaId,
            relatedId: id,
          },
        }),
      )
    }
  }

  clearSelection(transactors)

  return changes
}

export const addOnePanel: TransactionOperation<
  Point2d & Scale2d & { r: number; orientation?: number | null }
> = (transactors, protoPanel) => {
  const changes = addPanel(transactors, protoPanel)
  divergeTimeline(transactors, changes)
}

export const useAddOnePanel = (): ((
  p: Point2d & Scale2d & { r: number; orientation?: number | null },
) => void) =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => (payload) => addOnePanel(transactors, payload),
  )
